<script setup>
import { ref, computed } from 'vue'
import { Upload, X, FileVideo, FileAudio, FileImage } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { toast } from '@/components/ui/toast'
import { generateThumbnail } from '@/utils/fileValidation'
import { mediaConfig, getAcceptedFormats, validateFile, formatFileSize } from '@/config/media'
import { uploadResource } from '@/services/resourcesApi'

const props = defineProps({
  featureCode: {
    type: String,
    default: null
  },
  maxFiles: {
    type: Number,
    default: () => mediaConfig.upload.maxFiles
  },
  maxFileSize: {
    type: Number,
    default: () => mediaConfig.maxSize.total
  },
  allowedTypes: {
    type: Array,
    default: () => ['image', 'video', 'audio']
  },
  multiple: {
    type: Boolean,
    default: true
  },
  deferUpload: {
    type: Boolean,
    default: false
  },
  compact: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['uploaded', 'error', 'remove'])

const dropZoneMinHeight = computed(() => props.compact ? '80px' : '200px')

const isDragging = ref(false)
const fileInput = ref(null)
const uploadQueue = ref([])

const isUploading = computed(() => {
  return uploadQueue.value.some(item => item.status === 'uploading')
})

const completedUploads = computed(() => {
  return uploadQueue.value.filter(item => item.status === 'completed')
})

const acceptString = computed(() => {
  // Get accepted formats based on allowed types
  if (props.allowedTypes.length === 3) {
    return getAcceptedFormats() // All formats
  }

  return props.allowedTypes.map(type => getAcceptedFormats(type)).join(',')
})

const supportedFormatsText = computed(() => {
  const parts = []

  if (props.allowedTypes.includes('image')) {
    parts.push(`Images: ${mediaConfig.formats.image.extensions.join(', ')}`)
  }
  if (props.allowedTypes.includes('video')) {
    parts.push(`Videos: ${mediaConfig.formats.video.extensions.join(', ')}`)
  }
  if (props.allowedTypes.includes('audio')) {
    parts.push(`Audio: ${mediaConfig.formats.audio.extensions.join(', ')}`)
  }

  return parts.join(' | ')
})

const handleDragEnter = (e) => {
  e.preventDefault()
  e.stopPropagation()
  isDragging.value = true
}

const handleDragLeave = (e) => {
  e.preventDefault()
  e.stopPropagation()
  // Only set to false if we're leaving the drop zone entirely
  if (e.target.classList.contains('drop-zone')) {
    isDragging.value = false
  }
}

const handleDragOver = (e) => {
  e.preventDefault()
  e.stopPropagation()
}

const handleDrop = async (e) => {
  e.preventDefault()
  e.stopPropagation()
  isDragging.value = false

  const files = Array.from(e.dataTransfer.files)
  await processFiles(files)
}

const handleFileSelect = async (e) => {
  const files = Array.from(e.target.files)
  await processFiles(files)
  // Reset input to allow selecting the same file again
  fileInput.value.value = ''
}

const processFiles = async (files) => {
  // Only accept as many files as fit within the maxFiles limit
  const remainingSlots = props.maxFiles - uploadQueue.value.length
  let acceptedFiles = files
  let rejectedFiles = []
  if (files.length > remainingSlots) {
    acceptedFiles = files.slice(0, remainingSlots)
    rejectedFiles = files.slice(remainingSlots)
    toast({
      title: 'Too many files',
      description: `Only ${remainingSlots} more file(s) can be uploaded. ${rejectedFiles.length} file(s) were not added.`,
      variant: 'destructive'
    })
  }

  for (const file of acceptedFiles) {
    // Validate file
    const validation = validateFile(file)

    if (!validation.valid) {
      toast({
        title: 'Invalid file',
        description: `${file.name}: ${validation.error}`,
        variant: 'destructive'
      })
      continue
    }

    // Check if file type is allowed
    if (!props.allowedTypes.includes(validation.type)) {
      toast({
        title: 'File type not allowed',
        description: `${file.name}: ${validation.type} files are not allowed`,
        variant: 'destructive'
      })
      continue
    }

    // Generate thumbnail
    const thumbnail = await generateThumbnail(file)

    // Add to upload queue
    const uploadItem = {
      id: Date.now() + Math.random(),
      file,
      fileType: validation.type,
      thumbnail,
      progress: 0,
      status: 'pending',
      error: null
    }

    uploadQueue.value.push(uploadItem)

    // Start upload immediately unless deferred
    if (!props.deferUpload) {
      uploadFile(uploadItem)
    }
  }
}

const uploadFile = async (uploadItem) => {
  try {
    uploadItem.status = 'uploading'

    const response = await uploadResource(
      uploadItem.file,
      props.featureCode,
      (progressEvent) => {
        uploadItem.progress = progressEvent.total > 0 ? Math.round((progressEvent.loaded * 100) / progressEvent.total) : 0
      }
    )

    uploadItem.status = 'completed'
    uploadItem.resources = response.resources
    uploadItem.uuid = response.uuid
    emit('uploaded', response)

    toast({
      title: 'File uploaded',
      description: mediaConfig.messages.uploadSuccess
    })
  } catch (error) {
    uploadItem.status = 'error'
    uploadItem.error = error.message
    emit('error', error)

    toast({
      title: 'Upload failed',
      description: `${uploadItem.file.name}: ${error.message}`,
      variant: 'destructive'
    })
  }
}

const removeFromQueue = (uploadItem) => {
  const index = uploadQueue.value.findIndex(item => item.id === uploadItem.id)
  if (index !== -1) {
    // Revoke thumbnail URL to free memory
    if (uploadItem.thumbnail) {
      URL.revokeObjectURL(uploadItem.thumbnail)
    }
    uploadQueue.value.splice(index, 1)

    if (uploadItem.resource) {
      emit('remove', uploadItem.resource)
    }
  }
}

const openFileDialog = () => {
  fileInput.value?.click()
}

// Method to start deferred uploads
const startUpload = async (featureCode = null) => {
  // Update featureCode if provided
  const uploadFeatureCode = featureCode || props.featureCode

  if (!uploadFeatureCode && !props.deferUpload) {
    toast({
      title: 'Cannot upload',
      description: 'Feature ID is required for upload',
      variant: 'destructive'
    })
    return false
  }

  // Upload all pending items concurrently
  const pendingItems = uploadQueue.value.filter(item => item.status === 'pending')

  const uploadPromises = pendingItems.map(item =>
    uploadFileWithFeatureCode(item, uploadFeatureCode)
  )

  await Promise.allSettled(uploadPromises)

  return true
}

// Upload file with specific feature ID
const uploadFileWithFeatureCode = async (uploadItem, featureCode) => {
  try {
    uploadItem.status = 'uploading'

    const response = await uploadResource(
      uploadItem.file,
      featureCode,
      (progressEvent) => {
        uploadItem.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
      }
    )

    uploadItem.status = 'completed'
    uploadItem.resources = response.resources
    uploadItem.uuid = response.uuid
    emit('uploaded', response)

    toast({
      title: 'File uploaded',
      description: mediaConfig.messages.uploadSuccess
    })
  } catch (error) {
    uploadItem.status = 'error'
    uploadItem.error = error.message
    emit('error', error)

    toast({
      title: 'Upload failed',
      description: `${uploadItem.file.name}: ${error.message}`,
      variant: 'destructive'
    })
  }
}

// Clear all queued files
const clearQueue = () => {
  // Revoke all thumbnail URLs
  uploadQueue.value.forEach(item => {
    if (item.thumbnail) {
      URL.revokeObjectURL(item.thumbnail)
    }
  })
  uploadQueue.value = []
}

// Expose methods to parent
defineExpose({
  startUpload,
  clearQueue,
  getQueuedFiles: () => uploadQueue.value.filter(item => item.status === 'pending'),
  addFiles: processFiles
})

const getFileIcon = (fileType) => {
  switch (fileType) {
    case 'image':
      return FileImage
    case 'video':
      return FileVideo
    case 'audio':
      return FileAudio
    default:
      return Upload
  }
}
</script>

<template>
  <div class="media-upload" :class="{ 'max-w-[50%]': compact }">
    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      :multiple="multiple"
      :accept="acceptString"
      @change="handleFileSelect"
      class="hidden"
    />

    <!-- Drop zone -->
    <div
      class="drop-zone border-2 border-dashed rounded-lg transition-all duration-200"
      :class="[
        compact ? 'p-4' : 'p-8',
        {
          'border-primary bg-primary/5': isDragging,
          'border-border hover:border-muted-foreground': !isDragging
        }
      ]"
      @dragenter="handleDragEnter"
      @dragleave="handleDragLeave"
      @dragover="handleDragOver"
      @drop="handleDrop"
      @click="openFileDialog"
    >
      <div class="flex flex-col items-center justify-center text-center cursor-pointer">
        <Upload :class="compact ? 'w-6 h-6 mb-2 text-muted-foreground' : 'w-12 h-12 mb-4 text-muted-foreground'" />
        <p :class="compact ? 'text-sm text-muted-foreground mb-1' : 'text-lg font-medium mb-2'">
          Drop files here or click to browse
        </p>
        <p class="text-xs text-muted-foreground max-w-75">
          {{ supportedFormatsText }}
        </p>
        <p class="text-xs text-muted-foreground mt-1">
          Max file size: {{ formatFileSize(maxFileSize) }}
        </p>
      </div>
    </div>

    <!-- Upload queue -->
    <div v-if="uploadQueue.length > 0" class="mt-4 space-y-2">
      <div
        v-for="item in uploadQueue"
        :key="item.id"
        class="flex items-center gap-3 p-3 bg-muted rounded-lg"
      >
        <!-- Thumbnail or icon -->
        <div class="flex-shrink-0 w-12 h-12">
          <img
            v-if="item.thumbnail"
            :src="item.thumbnail"
            :alt="item.file.name"
            class="w-full h-full object-cover rounded"
          />
          <component
            v-else
            :is="getFileIcon(item.fileType)"
            class="w-full h-full text-muted-foreground"
          />
        </div>

        <!-- File info and progress -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between mb-1">
            <p class="text-sm font-medium truncate max-w-48">{{ item.file.name }}</p>
            <span class="text-xs text-muted-foreground">{{ formatFileSize(item.file.size) }}</span>
          </div>

          <!-- Loading spinner -->
          <div v-if="item.status === 'uploading'" class="mb-1">
            <Spinner class="h-4 w-4" />
          </div>

          <!-- Status -->
          <p class="text-xs" :class="{
            'text-muted-foreground': item.status === 'pending',
            'text-blue-500': item.status === 'uploading',
            'text-green-500': item.status === 'completed',
            'text-red-500': item.status === 'error'
          }">
            <span v-if="item.status === 'pending' && deferUpload">Ready to upload</span>
            <span v-else-if="item.status === 'pending'">Waiting...</span>
            <span v-else-if="item.status === 'uploading'">Uploading {{ item.progress }}%</span>
            <span v-else-if="item.status === 'completed'">Uploaded</span>
            <span v-else-if="item.status === 'error'">{{ item.error }}</span>
          </p>
        </div>

        <!-- Remove button -->
        <Button
          v-if="item.status !== 'uploading'"
          @click.stop="removeFromQueue(item)"
          variant="ghost"
          size="sm"
          class="flex-shrink-0"
        >
          <X class="w-4 h-4" />
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.drop-zone {
  min-height: v-bind(dropZoneMinHeight);
}
</style>
